import { useState, useEffect, useContext } from "react";
import { SiteContext } from "../contexts/SiteContext";
import placeholder from "../inc/placeholder.jpg";
import fetchSite from "../utils/fetchSite";
import { useParams, Link } from "react-router-dom";
import {
  Paper,
  Button,
  Typography,
  makeStyles,
  Fade,
  Grid,
  Card,
  CardContent,
  CardActionArea,
  CardMedia
} from "@material-ui/core";
import LayersIcon from "@material-ui/icons/Layers";
import Loading from "../components/Loading";
import Header from "../components/Header";
import AppBar from "../components/AppBar";
import Breadcrumbs from "../components/Breadcrumbs";
import AdminBar from "../components/AdminBar";
import Banner from "../components/Banner";

function Entries() {
  const [loading, setLoading] = useState(true);
  const [allEntries, setAllEntries] = useState();
  const [entries, setEntries] = useState();
  const [themes, setThemes] = useState();
  const [regions, setRegions] = useState();
  const [filters, setFilters] = useState({ themes: [], regions: [] })
  const { selectedSite, setSelectedSite } = useContext(SiteContext);
  const { siteSlug } = useParams();

  const useStyles = makeStyles((theme) => ({
    root: {
      flex: 1,
      paddingBottom: "4rem",
    },
    main: {
      margin: "0 auto",
      padding: "1rem",
      paddingTop: 0,
      maxWidth: process.env.REACT_APP_MAX_WIDTH,
    },
    paper: {
      padding: "1rem",
    },
    flex: {
      display: 'flex',
      marginTop: '2rem',
      gap: '1rem',
      justifyContent: 'center',
      [theme.breakpoints.down('md')]: {
        flexDirection: 'column'
      }
    },
    filtersWrap: {
      [theme.breakpoints.up('md')]: {
        flex: 1,
      },
    },
    filters: {
      [theme.breakpoints.up('md')]: {
        position: 'sticky',
      },
      top: '1rem'
    },
    entries: {
      [theme.breakpoints.up('md')]: {
        flex: 4
      }
    },
    link: {
      textDecoration: 'none',
      color:
        selectedSite && selectedSite.secondaryColor
          ? selectedSite.secondaryColor
          : theme.palette.secondary.main,
    },
    media: { paddingBottom: "100%" },
    checkbox: {
      cursor: 'pointer'
    },
    inputGroup: {
      marginBottom: '.5rem',
      fontSize: '1.2em',
      cursor: 'pointer',
      '& label': {
        marginLeft: '.5rem',
        cursor: 'pointer'
      }
    }
  }));

  const classes = useStyles();

  // This effect sets the selectedSite based on the :siteSlug (first url parameter)
  useEffect(() => {
    fetchSite(siteSlug, setSelectedSite);
  }, [siteSlug, setSelectedSite]);

  // This effect sets the entries and uses the selectedSite._id to make sure it's on the right site
  useEffect(() => {
    async function fetchEntries() {
      console.log(selectedSite._id);
      let res = await fetch(
        `${process.env.REACT_APP_API_URL}/entries?unit.site=${selectedSite._id}&sort=+title`,
        { credentials: "include" }
      );
      res = await res.json();
      if (res.success) {
        let filtered = res.data.filter(entry => {
          return (entry.unit.site._id === selectedSite._id);
        });
        setAllEntries(filtered);
      } else {
        console.error(res.error);
      }
    }
    async function fetchThemes() {
      let res = await fetch(
        `${process.env.REACT_APP_API_URL}/themes?site=${selectedSite._id}&sort=+name`,
        { credentials: "include" }
      );
      res = await res.json();
      if (res.success) {
        setThemes(res.data);
      } else {
        console.error(res.error);
      }
    }
    async function fetchRegions() {
      let res = await fetch(
        `${process.env.REACT_APP_API_URL}/regions?site=${selectedSite._id}&sort=+name`,
        { credentials: "include" }
      );
      res = await res.json();
      if (res.success) {
        setRegions(res.data);
      } else {
        console.error(res.error);
      }
    }
    if (selectedSite) {
      fetchEntries();
      fetchThemes();
      fetchRegions();
      setLoading(false);
    }
  }, [selectedSite]);

  useEffect(() => {
    if (allEntries) {
      const filteredEntries = allEntries.filter((entry) => {
        const entryThemes = entry.themes.map(theme => theme.name);
        const entryRegions = entry.regions.map(region => region.name);

        // Your existing filtering logic for themes
        var containsThemes = true;
        if (filters.themes.length > 0) {
          containsThemes = filters.themes.some((theme) => entryThemes.includes(theme))
        }

        // New filtering logic for regions
        var containsRegions = true;
        if (filters.regions.length > 0) {
          containsRegions = filters.regions.some((region) => entryRegions.includes(region))
        }

        // Combine both theme and region filtering
        if (containsThemes && containsRegions) return true;
        return false;
      });

      setEntries(filteredEntries);
    }
  }, [allEntries, filters])

  const handleThemes = (event) => {
    let updatedList = [...filters.themes];
    if (event.target.checked) {
      updatedList = [...filters.themes, event.target.value];
    } else {
      updatedList.splice(filters.themes.indexOf(event.target.value), 1);
    }
    setFilters({ ...filters, themes: updatedList })
  }

  // Add a new function to handle regions
  const handleRegions = (event) => {
    let updatedList = [...filters.regions];
    if (event.target.checked) {
      updatedList = [...filters.regions, event.target.value];
    } else {
      updatedList.splice(filters.regions.indexOf(event.target.value), 1);
    }
    setFilters({ ...filters, regions: updatedList });
  }

  if (loading || !selectedSite || !entries || !themes || !allEntries) {
    return <Loading />;
  }

  return (
    <Fade in={typeof entries != "undefined"}>
      <div className={classes.root}>
        <AdminBar />
        <Header />
        <AppBar />
        <Banner />
        <Breadcrumbs
          links={[
            {
              name: "Entries",
              url: `/${selectedSite.slug}/entries`,
            },
          ]}
        />
        <main className={classes.main}>
          <Typography
            className={classes.title}
            gutterBottom
            component="h2"
            variant="h4"
          >
            <LayersIcon /> All Entries
          </Typography>
          <div className={classes.flex}>
            <div className={classes.filtersWrap}>
              <Paper className={classes.filters + " " + classes.paper}>
                <Typography variant="h5" component="p" style={{ marginBottom: '1rem' }}>Filters</Typography>
                <Typography variant="h6" component="p" style={{ marginBottom: '1rem' }}>Themes</Typography>
                {themes.map((theme, index) => {
                  return (
                    <div key={index} className={classes.inputGroup}>
                      <input className={classes.checkbox} type="checkbox" id={theme.name} value={theme.name} onChange={handleThemes} checked={filters.themes.includes(theme.name)} />
                      <label htmlFor={theme.name}>{theme.name}</label>
                    </div>
                  )
                })}
                <Typography variant="h6" component="p" style={{ marginTop: '1rem', marginBottom: '1rem' }}>Regions</Typography>

                {selectedSite && selectedSite.regionsImage &&
                  <Typography
                    style={{ marginBottom: "1rem" }}
                    component="h3"
                    variant="p"
                  >
                    <a href={`${process.env.REACT_APP_FILE_URL}/${selectedSite.regionsImage.fileName}`}>View Regions Map</a>
                  </Typography>
                }

                {regions.map((region, index) => {
                  return (
                    <div key={index} className={classes.inputGroup}>
                      <input className={classes.checkbox} type="checkbox" id={region.name} value={region.name} onChange={handleRegions} checked={filters.regions.includes(region.name)} />
                      <label htmlFor={region.name}>{region.name}</label>
                    </div>
                  )
                })}
                <Button style={{ marginTop: '1rem' }} onClick={() => { setFilters({ themes: [] }) }}>
                  Clear Filters
                </Button>
              </Paper>
            </div>
            <Paper className={classes.entries + " " + classes.paper}>
              <Typography style={{ marginBottom: '1rem' }}>{entries.length} entries found</Typography>
              <Grid container spacing={3}>
                {
                  entries.map((entry, index) => {
                    return (
                      <Grid item key={index} xs={12} sm={6} md={4} lg={3}>
                        <Card elevation={0} className={classes.card} aria-label={entry.title}>
                          <Link className={classes.link} to={`/${selectedSite.slug}/entries/${entry.slug}`}>
                            <CardActionArea>
                              <CardMedia
                                className={classes.media}
                                image={
                                  entry.featuredImage
                                    ? `${process.env.REACT_APP_FILE_URL}/${entry.featuredImage.thumbnailName}`
                                    : placeholder
                                }
                                title={entry.title}
                              />
                              <CardContent className={classes.entryContent}>
                                <Typography align="center" component="h4">
                                  {entry.title}
                                </Typography>
                              </CardContent>
                            </CardActionArea>
                          </Link>
                        </Card>
                      </Grid>
                    )
                  })
                }
              </Grid>
            </Paper>
          </div>
        </main>
      </div>
    </Fade>
  );
}

export default Entries;
